import { useState, useEffect } from 'react';
import { fetchDentallyAppointmentReasons } from '../utils/dentallyApi';
import { callApi } from '../utils/apiCall';
import { DENTALLY_REASON_OPTIONS } from '../utils/dentallyUtils';

// Module-level cache — shared across all component instances.
// Keyed by practiceId, expires after 60s to stay reasonably fresh.
const cache = {};
const CACHE_TTL_MS = 60000;

function getCached(practiceId) {
    const entry = cache[practiceId];
    if (entry && Date.now() - entry.ts < CACHE_TTL_MS) return entry.data;
    return null;
}

function setCache(practiceId, data) {
    cache[practiceId] = { data, ts: Date.now() };
}

/**
 * Fetches the practice's actual Dentally appointment reasons, merges with
 * the static fallback list, and builds a reason→CRM treatment name map.
 * Results are cached for 60s across all components using this hook.
 *
 * Usage:
 *   const { reasons, reasonTreatmentMap } = useDentallyReasonOptions(practiceId);
 */
export default function useDentallyReasonOptions(practiceId) {
    const cached = getCached(practiceId);
    const [reasons, setReasons] = useState(cached?.reasons || DENTALLY_REASON_OPTIONS);
    const [reasonTreatmentMap, setReasonTreatmentMap] = useState(cached?.reasonTreatmentMap || {});

    useEffect(() => {
        if (!practiceId) return;

        // Use cache if available
        const hit = getCached(practiceId);
        if (hit) {
            setReasons(hit.reasons);
            setReasonTreatmentMap(hit.reasonTreatmentMap);
            return;
        }

        let cancelled = false;

        Promise.all([
            fetchDentallyAppointmentReasons(practiceId).catch(() => []),
            callApi(
                `/api/v1/practices/${practiceId}/treatments/?fields={id,treatment_item{id,name},dentally_appointment_reason}&enabled=true&dentally_treatment_id__isnull=false&limit=200`
            ).then((r) => (r.ok ? r.data?.results || [] : [])).catch(() => []),
        ]).then(([apiReasons, crmTreatments]) => {
            if (cancelled) return;

            const map = {};
            crmTreatments.forEach((t) => {
                if (t.dentally_appointment_reason) {
                    map[t.dentally_appointment_reason] = t.treatment_item?.name || '';
                }
            });

            const reasonNames = (apiReasons || [])
                .map((r) => r.name || r.reason || (typeof r === 'string' ? r : null))
                .filter(Boolean);

            let mergedReasons = DENTALLY_REASON_OPTIONS;
            if (reasonNames.length > 0) {
                const seen = new Set(reasonNames.map((r) => r.toLowerCase()));
                const extras = DENTALLY_REASON_OPTIONS.filter((r) => !seen.has(r.toLowerCase()));
                mergedReasons = [...reasonNames, ...extras];
            }

            setReasons(mergedReasons);
            setReasonTreatmentMap(map);
            setCache(practiceId, { reasons: mergedReasons, reasonTreatmentMap: map });
        });

        // eslint-disable-next-line consistent-return
        return () => { cancelled = true; };
    }, [practiceId]);

    return { reasons, reasonTreatmentMap };
}
